import { OverlayModel } from '../../../models/overlay.model'

/**
 * Overlay Singleton - Picks up click events on the page and opens associated page/body overlays
 */
class GMXOverlaySingleton {
	intersectionObserver: IntersectionObserver
	mutationObserver: MutationObserver

	constructor() {
		// @ts-expect-error:  adding global class to window
		if (!window.gmxOverlaySingleton) {
			// @ts-expect-error:  adding global class to window
			window.gmxOverlaySingleton = this

			this.setupOverlayShowListener()
			this._addOverlayObserver()
		} else {
			window.console.log('Tried to instantiate overlay singleton...already instantiated')
		}
	}

	/**
	 * Method to setup a click event listener on the body, looking for elements that were clicked that are
	 * to open an overlay on the page, ie, has attribute [data-overlay-reveal-id].  If an overlay id is found,
	 * then we'll look to see if its already loaded on the page, else, fetch and insert at the bottom of the body
	 */
	public setupOverlayShowListener(root: ShadowRoot | HTMLElement = document.body): void {
		root.addEventListener('click', e => {
			let target = e.target as HTMLElement

			let targetOverlayId = target.getAttribute('data-overlay-reveal-id')

			if (!targetOverlayId) {
				// Overlay id not found, look up through ancestors to see if a child element captured the click event
				target = target.closest('[data-overlay-reveal-id]') as HTMLElement

				if (target) {
					targetOverlayId = target.getAttribute('data-overlay-reveal-id')
				}
			}

			// If we didn't find a target overlay id, the click event wasn't for an overlay so pass through without
			// doing anything
			if (targetOverlayId) {
				const targetOverlayEl = root.querySelector(`[id="${targetOverlayId}"]`)

				if (targetOverlayEl) {
					targetOverlayEl.setAttribute('data-show', '')
				} else {
					window.console.warn(`Error:  Overlay with id ${targetOverlayId} not found..try to fetch`)

					const targetOverlayType = target.getAttribute('data-overlay-type') || 'content-fit'

					this._insertNewOverlayElement(targetOverlayId, targetOverlayType, true, root)
				}
			}
		})
	}

	/**
	 * Setup an IntersectionObserver to observe for when overlay trigger elements (those that have attributte
	 * [data-overlay-reveal-id]) come close to the viewport and if observed, look to see if its already been loaded
	 * and if not, load and insert at the bottom of the body.
	 *
	 * @private
	 */
	private _addOverlayObserver(): void {
		this.intersectionObserver = new IntersectionObserver(entries => {
			entries.forEach(entry => {

			if (entry.isIntersecting) {
				const targetEl = entry.target
				const targetOverlayId = targetEl.getAttribute('data-overlay-reveal-id'),
					targetOverlayType = targetEl.getAttribute('data-overlay-type') || 'content-fit'

				if (targetOverlayId) {
					// If the target overlay id exists and the element already exists, pass through as it is
					// already in the document.  Else if it isn't found, load and insert at the bottom of the body
					if (document.getElementById(targetOverlayId) === null) {
						// Only insert a new element if you didn't find the one with the id you are looking for
						this._insertNewOverlayElement(targetOverlayId, targetOverlayType, false)
					}
				}

				this.intersectionObserver.unobserve(targetEl)
			}
		})}, {
			root: null,
			rootMargin: '100px',
			threshold: 0,
		})

		const overlayTriggerEls = document.body.querySelectorAll('[data-overlay-reveal-id]')

		overlayTriggerEls.forEach(el => {
			this.intersectionObserver.observe(el)
		})

		this.mutationObserver = new MutationObserver(this._onDOMMutation.bind(this))

		this.mutationObserver.observe(document.getElementsByTagName('body')[0], {
			childList: true,
			subtree: true
		})
	}

	private _onDOMMutation(mutations: MutationRecord): void {
		if (Array.isArray(mutations)) {
			mutations.forEach(mut => {
				const target = mut.target
				const addedNode = mut.addedNodes && mut.addedNodes[0]

				if (target && target.nodeType === 1) {
					if (addedNode) {

						let overlayTriggerEls
						if (addedNode.nodeType === 1) {
							overlayTriggerEls = addedNode.querySelectorAll('[data-overlay-reveal-id]')

							overlayTriggerEls.forEach((te: HTMLElement) => {
								this.intersectionObserver.observe(te)
							})
						} else if (addedNode.nodeType === 3) {
							if (addedNode.parentElement) {
								overlayTriggerEls = addedNode.parentElement.querySelectorAll('[data-overlay-reveal-id]')
							}
						}

						if (overlayTriggerEls) {
							overlayTriggerEls.forEach((te: HTMLElement) => {
								this.intersectionObserver.observe(te)
							})
						}
					}
				}
			})
		}
	}

	/**
	 * Insert a new overlay element at the bottom of the document/body
	 * @param overlayPartialUrl {string} - url of the overlay content
	 * @param targetOverlayType {string} - type of overlay, to determine the overlay treatment/UX
	 * @param showImmediate {boolean} - if set to true, tells the overlay that once you've loaded the content, immediately show/open the overlay
	 * @private
	 */
	private _insertNewOverlayElement(overlayPartialUrl: string, targetOverlayType: string, showImmediate: boolean, root: ShadowRoot | HTMLElement = document.body): void {
		const newOverlayEl = document.createElement('gmx-overlay')
		newOverlayEl.setAttribute('id', overlayPartialUrl)
		newOverlayEl.setAttribute('data-overlay-type', targetOverlayType)

		if (showImmediate) {
			newOverlayEl.setAttribute('data-show', '')
		}

		root.appendChild(newOverlayEl)
	}

	/**
	 * For the Injector component, want to preload all overlays within the ShadowRoot of each calling injector.
	 * @param root
	 * @param overlays
	 */
	public prefetchOverlaysForInjector(root: ShadowRoot, overlays: OverlayModel[]): void {
		overlays.forEach(overlay => {
			const newOverlayEl = document.createElement('gmx-overlay')
			newOverlayEl.setAttribute('id', overlay.id)
			newOverlayEl.setAttribute('data-overlay-type', overlay.type)

			root.appendChild(newOverlayEl)
		})
	}
}

const overlaySingleton = Object.freeze(new GMXOverlaySingleton())
export default overlaySingleton
